import React, {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import Icon from '../../../ui/Icon';
import IconButton from '../../../ui/IconButton';
import './ConsumableEffectsHud.css';

function field(source, camelName, pascalName) {
  return source?.[camelName] ?? source?.[pascalName];
}

function timestampMs(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return Number.NaN;
  const numeric = Number(value);
  if (Number.isFinite(numeric) && /^\d+$/.test(value.trim())) return numeric;
  return Date.parse(value);
}

function displayNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value ?? '').trim() || '0';
  return Number(numeric.toFixed(2)).toString();
}

export function formatEffectAmount(effect) {
  const rawAmount = effect?.displayAmount ?? effect?.DisplayAmount ?? 0;
  const numericAmount = Number(rawAmount);
  const amount = Number.isFinite(numericAmount)
    ? `${numericAmount >= 0 ? '+' : ''}${displayNumber(numericAmount)}`
    : String(rawAmount).trim();
  const unit = String(
    effect?.displayUnit ?? effect?.DisplayUnit ?? '',
  ).trim();

  if (!unit) return amount;
  if (unit === '%' || unit.startsWith('/')) return `${amount}${unit}`;
  return `${amount} ${unit}`;
}

export function formatEffectCountdown(milliseconds) {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds)
      .padStart(2, '0')}`;
  }
  if (minutes > 0) {
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }
  return `${seconds}s`;
}

export function effectIconName(effect) {
  const target = `${effect?.targetKey || ''} ${effect?.targetName || ''}`
    .toLowerCase();
  if (
    target.includes('health')
    || target.includes('vitality')
    || target.includes('regen')
    || /\bhp\b/.test(target)
  ) return 'health';
  if (
    target.includes('damage')
    || target.includes('strength')
    || target.includes('crit')
  ) return 'damage';
  if (
    target.includes('armor')
    || target.includes('resilience')
    || target.includes('dodge')
  ) return 'armor';
  if (
    target.includes('speed')
    || target.includes('flee')
  ) return 'speed';
  return 'effect';
}

export function normalizeConsumableEffectQueue(user) {
  const queue = field(
    user,
    'consumableEffectQueue',
    'ConsumableEffectQueue',
  );
  if (!Array.isArray(queue)) return [];

  return queue.map((effect, index) => {
    const targetKind = String(field(
      effect,
      'targetKind',
      'TargetKind',
    ) || 'Effect');
    const targetName = String(field(
      effect,
      'targetName',
      'TargetName',
    ) || field(
      effect,
      'displayName',
      'DisplayName',
    ) || 'Temporary effect');
    const targetId = field(effect, 'targetId', 'TargetId')
      ?? field(effect, 'statId', 'StatId')
      ?? field(effect, 'attributeKey', 'AttributeKey')
      ?? field(effect, 'targetKey', 'TargetKey')
      ?? targetName;
    const startsAtMs = timestampMs(field(
      effect,
      'startsAtUtc',
      'StartsAtUtc',
    ));
    const expiresAtMs = timestampMs(field(
      effect,
      'expiresAtUtc',
      'ExpiresAtUtc',
    ));

    return {
      displayAmount: field(effect, 'displayAmount', 'DisplayAmount')
        ?? field(effect, 'amount', 'Amount'),
      displayUnit: field(effect, 'displayUnit', 'DisplayUnit')
        ?? field(effect, 'unit', 'Unit'),
      expiresAtMs,
      index,
      sourceItemName: String(field(
        effect,
        'sourceItemName',
        'SourceItemName',
      ) || 'Unknown consumable'),
      startsAtMs,
      targetKey: `${targetKind}:${String(targetId)}`,
      targetName,
    };
  }).filter(effect => (
    Number.isFinite(effect.startsAtMs)
    && Number.isFinite(effect.expiresAtMs)
    && effect.expiresAtMs > effect.startsAtMs
  )).sort((left, right) => (
    left.startsAtMs - right.startsAtMs
    || left.expiresAtMs - right.expiresAtMs
    || left.index - right.index
  ));
}

function activeEffectGroups(effects, nowMs) {
  const byTarget = new Map();
  effects.forEach((effect) => {
    if (effect.expiresAtMs <= nowMs) return;
    const existing = byTarget.get(effect.targetKey) || [];
    existing.push(effect);
    byTarget.set(effect.targetKey, existing);
  });

  return Array.from(byTarget.entries()).flatMap(([targetKey, segments]) => {
    const activeIndex = segments.findIndex(segment => (
      segment.startsAtMs <= nowMs && nowMs < segment.expiresAtMs
    ));
    if (activeIndex < 0) return [];
    return [{
      active: segments[activeIndex],
      queued: segments.filter((_, index) => index !== activeIndex),
      targetKey,
    }];
  });
}

export default function ConsumableEffectsHud({
  serverTimeOffsetMs = 0,
  user,
  variant = 'world',
}) {
  const effects = useMemo(
    () => normalizeConsumableEffectQueue(user),
    [user],
  );
  const offsetMs = Number(serverTimeOffsetMs) || 0;
  const [nowMs, setNowMs] = useState(() => Date.now() + offsetMs);
  const [selectedTargetKey, setSelectedTargetKey] = useState('');
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const buttonRefs = useRef(new Map());
  const detailsId = `${useId()}-consumable-effect-details`;
  const latestExpiryMs = effects.reduce(
    (latest, effect) => Math.max(latest, effect.expiresAtMs),
    0,
  );
  const groups = useMemo(
    () => activeEffectGroups(effects, nowMs),
    [effects, nowMs],
  );
  const selectedGroup = groups.find(
    group => group.targetKey === selectedTargetKey,
  );
  const selectedGroupTargetKey = selectedGroup?.targetKey || '';
  const toggleDetails = (targetKey) => {
    setSelectedTargetKey(current => (
      current === targetKey ? '' : targetKey
    ));
  };

  useEffect(() => {
    const currentServerTime = Date.now() + offsetMs;
    setNowMs(currentServerTime);
    if (latestExpiryMs <= currentServerTime) return undefined;

    const timer = window.setInterval(() => {
      const nextServerTime = Date.now() + offsetMs;
      setNowMs(nextServerTime);
      if (nextServerTime >= latestExpiryMs) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [latestExpiryMs, offsetMs]);

  useEffect(() => {
    if (!selectedTargetKey || selectedGroupTargetKey) return;
    setSelectedTargetKey('');
    const nextButton = buttonRefs.current.values().next().value;
    nextButton?.focus();
  }, [selectedGroupTargetKey, selectedTargetKey]);

  useEffect(() => {
    if (!selectedTargetKey) return undefined;
    panelRef.current?.querySelector('button')?.focus();

    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return;
      setSelectedTargetKey('');
      buttonRefs.current.get(selectedTargetKey)?.focus();
    };
    const closeOutside = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      const targetKey = selectedTargetKey;
      setSelectedTargetKey('');
      buttonRefs.current.get(targetKey)?.focus();
    };
    document.addEventListener('keydown', closeOnEscape);
    document.addEventListener('pointerdown', closeOutside);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('pointerdown', closeOutside);
    };
  }, [selectedTargetKey]);

  if (groups.length === 0) return null;

  const closeDetails = () => {
    const targetKey = selectedGroup?.targetKey;
    setSelectedTargetKey('');
    buttonRefs.current.get(targetKey)?.focus();
  };
  const safeVariant = variant === 'camp' ? 'camp' : 'world';

  return (
    <aside
      aria-label="Temporary consumable effects"
      className={[
        'consumable-effects-hud',
        `consumable-effects-hud--${safeVariant}`,
      ].join(' ')}
      data-floating-window-obstacle={
        safeVariant === 'world' ? 'true' : undefined
      }
      ref={rootRef}
    >
      <div
        aria-label="Active temporary effects"
        className="consumable-effects-hud__row"
        role="group"
      >
        {groups.map((group) => {
          const { active, queued, targetKey } = group;
          const amount = formatEffectAmount(active);
          const remaining = formatEffectCountdown(
            active.expiresAtMs - nowMs,
          );
          const expanded = selectedTargetKey === targetKey;
          const queueText = queued.length === 1
            ? '1 effect queued'
            : `${queued.length} effects queued`;
          return (
            <button
              aria-controls={detailsId}
              aria-expanded={expanded}
              aria-haspopup="dialog"
              aria-label={[
                `${active.targetName}, ${amount}`,
                `${remaining} remaining`,
                queueText,
              ].join('. ')}
              className="consumable-effects-hud__effect"
              key={targetKey}
              onClick={() => toggleDetails(targetKey)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                toggleDetails(targetKey);
              }}
              ref={(node) => {
                if (node) buttonRefs.current.set(targetKey, node);
                else buttonRefs.current.delete(targetKey);
              }}
              title={`Show ${active.targetName} effect details`}
              type="button"
            >
              <Icon name={effectIconName(active)} size={20} />
              <span
                aria-hidden="true"
                className="consumable-effects-hud__countdown"
              >
                {remaining}
              </span>
              {queued.length > 0 ? (
                <span
                  aria-hidden="true"
                  className="consumable-effects-hud__queue-badge"
                >
                  +{queued.length}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {selectedGroup ? (
        <section
          aria-labelledby={`${detailsId}-title`}
          aria-modal="false"
          className="consumable-effects-hud__details"
          id={detailsId}
          ref={panelRef}
          role="dialog"
        >
          <header className="consumable-effects-hud__details-header">
            <div>
              <span className="consumable-effects-hud__eyebrow">
                Temporary effect
              </span>
              <h2 id={`${detailsId}-title`}>
                {selectedGroup.active.targetName}
              </h2>
            </div>
            <IconButton
              className="consumable-effects-hud__close"
              icon="close"
              label={`Close ${selectedGroup.active.targetName} effect details`}
              onClick={closeDetails}
            />
          </header>
          <dl className="consumable-effects-hud__active-details">
            <div>
              <dt>Active bonus</dt>
              <dd>{formatEffectAmount(selectedGroup.active)}</dd>
            </div>
            <div>
              <dt>Remaining</dt>
              <dd>{formatEffectCountdown(
                selectedGroup.active.expiresAtMs - nowMs,
              )}</dd>
            </div>
            <div>
              <dt>Source</dt>
              <dd>{selectedGroup.active.sourceItemName}</dd>
            </div>
          </dl>
          {selectedGroup.queued.length > 0 ? (
            <>
              <h3>Queued effects</h3>
              <ol className="consumable-effects-hud__queue">
                {selectedGroup.queued.map(effect => (
                  <li key={`${effect.startsAtMs}:${effect.index}`}>
                    <span>
                      <strong>{formatEffectAmount(effect)}</strong>
                      {' '}
                      from {effect.sourceItemName}
                    </span>
                    <span>
                      Starts in {formatEffectCountdown(
                        effect.startsAtMs - nowMs,
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </>
          ) : (
            <p className="consumable-effects-hud__empty">
              No additional effect is queued.
            </p>
          )}
        </section>
      ) : null}
    </aside>
  );
}
